'use strict';

(() => {
  if (customElements.get('oluvie-reference-header')) return;
  customElements.define('oluvie-reference-header', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.detached = false;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.toggle = this.querySelector('[data-or-menu-toggle]');
      this.panel = this.querySelector('[data-or-menu-panel]');
      this.navPanels = [...this.querySelectorAll('[data-or-nav-panel]')];
      this.navOpeners = new Map();
      this.navScroll = new Map();
      this.animations = new Map();
      this.menuOffer = this.querySelector('[data-or-menu-offer]');
      this.menuOfferRows = [...this.querySelectorAll('[data-or-menu-tier]')];
      this.menuOfferVersion = 0;
      this.menuOfferPending = false;
      this.menuOfferUncertain = false;
      this.menuOfferRequest = null;
      try { this.menuOfferConfig = JSON.parse(this.querySelector('[data-or-menu-offer-config]')?.textContent || '{}'); } catch { this.menuOfferConfig = {}; }
      this.renderMenuOffer(this.menuOfferConfig.cartState);
      this.setupMenuMedia(options);
      document.addEventListener('focusin', (event) => {
        if (this.focusRestore && event.target !== this.focusRestore.target && event.target !== document.body && event.target !== document.documentElement) this.cancelFocusRestore();
      }, options);
      document.addEventListener('pointerdown', () => {
        this.dataset.orFocusInput = 'pointer';
        this.cancelFocusRestore();
      }, { ...options, capture: true });
      document.addEventListener('keydown', (event) => {
        if (!['Alt', 'Control', 'Meta', 'Shift'].includes(event.key)) this.dataset.orFocusInput = 'keyboard';
        this.cancelFocusRestore();
      }, { ...options, capture: true });
      window.addEventListener('pagehide', () => this.cancelFocusRestore(), options);
      window.addEventListener('beforeunload', () => this.cancelFocusRestore(), options);
      window.addEventListener('pageshow', () => this.refreshMenuOffer(), options);
      this.toggle?.addEventListener('click', () => {
        if (!this.openDialog(this.panel, this.toggle)) location.assign(this.toggle.dataset.menuFallback);
      }, options);
      this.querySelector('[data-or-menu-close]')?.addEventListener('click', () => this.closeDialog(this.panel), options);
      if (this.panel) {
        this.panel.addEventListener('cancel', (event) => { event.preventDefault(); this.closeDialog(this.panel); }, options);
        this.panel.addEventListener('click', (event) => { if (event.target === this.panel) this.closeDialog(this.panel); }, options);
        this.panel.addEventListener('close', () => this.afterClose(), options);
      }
      this.panel?.addEventListener('click', (event) => {
        const destination = event.target.closest('[data-or-nav-destination]');
        if (destination) this.rememberNavigation(event, destination);
        const forward = event.target.closest('[data-or-nav-open]');
        if (forward) this.showNavPanel(forward.dataset.orNavOpen, forward);
        const back = event.target.closest('[data-or-nav-back]');
        if (back) this.goBack(back.closest('[data-or-nav-panel]'));
      }, options);
      this.panel?.addEventListener('keydown', (event) => {
        this.trapMenuTab(event);
        const pane = event.target.closest('[data-or-nav-panel]');
        if (event.key === 'ArrowLeft' && pane?.dataset.orNavParent) { event.preventDefault(); this.goBack(pane); }
        if (event.key === 'ArrowRight') {
          const forward = event.target.closest('[data-or-nav-open]');
          if (forward) { event.preventDefault(); this.showNavPanel(forward.dataset.orNavOpen, forward); }
        }
      }, options);
      const receiveCart = (event) => this.receiveMenuCart(event.detail?.cart);
      document.addEventListener('oluvie:cart-added', receiveCart, options);
      document.addEventListener('oluvie:cart-updated', receiveCart, options);
      this.restoreNavigation();
    }

    trapMenuTab(event) {
      if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !this.panel?.open) return;
      const controls = [...this.panel.querySelectorAll('a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]')].filter((node) => {
        if (node.tabIndex < 0 || node.matches(':disabled, [aria-disabled="true"]') || node.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
        const style = getComputedStyle(node), rect = node.getBoundingClientRect();
        return style.visibility === 'visible' && style.display !== 'none' && rect.width > 0 && rect.height > 0;
      });
      if (!controls.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }
      const first = controls[0], last = controls[controls.length - 1], active = document.activeElement;
      if ((event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    disconnectedCallback() {
      this.detached = true;
      this.cancelFocusRestore();
      for (const animation of this.animations?.values() || []) animation.cancel();
      this.animations?.clear();
      this.abort?.abort();
      this.menuOfferRequest?.controller.abort();
      this.menuOfferRequest = null;
      this.cancelMenuMediaWarmup();
      this.menuMediaDecodes?.clear();
      if (this.panel?.open) this.panel.close();
      this.unlock(false);
      this.initialized = false;
    }

    animateDialog(dialog, opening) {
      this.animations.get(dialog)?.cancel();
      const surface = dialog.querySelector('.or-header__drawer');
      if (!surface?.animate || matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
      const offset = 'translateX(-100%)';
      const frames = [{ opacity: 0, transform: offset }, { opacity: 1, transform: 'none' }];
      const animation = surface.animate(opening ? frames : frames.slice().reverse(), { duration: 400, easing: 'cubic-bezier(.25,.8,.25,1)' });
      this.animations.set(dialog, animation);
      return animation;
    }

    openDialog(dialog, trigger) {
      this.cancelFocusRestore();
      if (!dialog || typeof dialog.showModal !== 'function') return false;
      this.animations.get(dialog)?.cancel();
      this.animations.delete(dialog);
      if (dialog.open) return true;
      try { dialog.showModal(); } catch { return false; }
      if (!this.scrollLock) {
        this.scrollLock = { x: scrollX, y: scrollY, overflow: document.body.style.overflow, menuClass: document.body.classList.contains('or-menu-open') };
        document.body.style.overflow = 'hidden';
        document.body.classList.add('or-menu-open');
      }
      this.returnFocus = trigger;
      this.dataset.open = 'true';
      this.toggle?.setAttribute('aria-expanded', String(Boolean(this.panel?.open)));
      this.animateDialog(dialog, true);
      this.panel.querySelector('[data-or-menu-close]')?.focus({ preventScroll: true });
      this.refreshMenuOffer();
      this.prepareMenuMedia(true);
      return true;
    }

    closeDialog(dialog) {
      this.clearNavigation();
      if (!dialog?.open) return;
      const animation = this.animateDialog(dialog, false);
      if (!animation) { dialog.close(); return; }
      animation.finished.then(() => {
        if (this.animations.get(dialog) === animation) { this.animations.delete(dialog); dialog.close(); }
      }).catch(() => {});
    }

    afterClose() {
      this.toggle?.setAttribute('aria-expanded', String(Boolean(this.panel?.open)));
      if (!this.panel?.open) this.unlock(true);
    }

    unlock(restoreFocus) {
      this.dataset.open = 'false';
      if (!this.scrollLock) return;
      const saved = this.scrollLock;
      this.scrollLock = null;
      document.body.style.overflow = saved.overflow;
      if (!saved.menuClass) document.body.classList.remove('or-menu-open');
      if (!this.detached) {
        window.scrollTo({ left: saved.x, top: saved.y, behavior: 'instant' });
        if (restoreFocus && this.returnFocus?.isConnected) this.restoreVisibleFocus(this.returnFocus);
      }
    }

    cancelFocusRestore() {
      if (this.focusRestore) cancelAnimationFrame(this.focusRestore.frame);
      this.focusRestore = null;
    }

    restoreVisibleFocus(target) {
      this.cancelFocusRestore();
      const pending = { target, url: location.href, focus: document.activeElement, frames: 0, visibleFrames: 0, frame: 0 };
      this.focusRestore = pending;
      const retry = () => {
        if (this.focusRestore !== pending) return;
        if (this.detached || !this.isConnected || !target.isConnected || location.href !== pending.url || document.querySelector('dialog[open]')) { this.cancelFocusRestore(); return; }
        const active = document.activeElement;
        if (active !== target && active !== pending.focus && active !== document.body && active !== document.documentElement) { this.cancelFocusRestore(); return; }
        const style = getComputedStyle(target), rect = target.getBoundingClientRect();
        if (style.visibility === 'visible' && style.display !== 'none' && rect.width > 0 && rect.height > 0 && !target.closest('[inert], [hidden]') && !target.matches(':disabled')) {
          if (active !== target) target.focus({ preventScroll: true });
          if (document.activeElement === target && ++pending.visibleFrames >= 2) { this.cancelFocusRestore(); return; }
        } else pending.visibleFrames = 0;
        if (++pending.frames >= 30) { this.cancelFocusRestore(); return; }
        pending.frame = requestAnimationFrame(retry);
      };
      pending.frame = requestAnimationFrame(retry);
    }

    showNavPanel(id, opener) {
      const target = this.navPanels.find((pane) => pane.id === id);
      if (!target) return;
      if (opener) this.navOpeners.set(id, opener);
      for (const pane of this.navPanels) {
        if (!pane.hidden) this.navScroll.set(pane.id, pane.scrollTop);
        pane.hidden = pane !== target;
      }
      target.scrollTop = this.navScroll.get(id) || 0;
      for (const button of this.querySelectorAll('[data-or-nav-open]')) button.setAttribute('aria-expanded', String(button.dataset.orNavOpen === id));
      if (target.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches) target.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease' });
      (target.querySelector('[data-or-nav-heading]') || target.querySelector('button, a'))?.focus({ preventScroll: true });
    }

    goBack(pane) {
      if (!pane?.dataset.orNavParent) return;
      this.showNavPanel(pane.dataset.orNavParent);
      this.navOpeners.get(pane.id)?.focus({ preventScroll: true });
    }

    navigationKey() {
      return `oluvie:menu-navigation:${globalThis.Shopify?.theme?.id || ''}:${this.panel?.id || ''}`;
    }

    clearNavigation() {
      try { sessionStorage.removeItem(this.navigationKey()); } catch {}
    }

    rememberNavigation(event, link) {
      if (this.dataset.orPageType !== 'product' || !matchMedia('(min-width: 768px)').matches || !this.panel?.open) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      try {
        const url = new URL(link.href, location.href);
        const pane = this.navPanels.find((item) => item.id === link.dataset.orNavDestination && item.dataset.orNavParent);
        if (url.origin !== location.origin || !pane) return;
        url.hash = '';
        sessionStorage.setItem(this.navigationKey(), JSON.stringify({ url: url.href, panel: pane.id, created: Date.now() }));
      } catch {}
    }

    restoreNavigation() {
      try {
        const raw = sessionStorage.getItem(this.navigationKey());
        this.clearNavigation();
        if (!raw || !matchMedia('(min-width: 768px)').matches) return;
        if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') return;
        const state = JSON.parse(raw);
        const url = new URL(state.url);
        const current = new URL(location.href);
        current.hash = '';
        const age = Date.now() - state.created;
        const pane = this.navPanels.find((item) => item.id === state.panel && item.dataset.orNavParent);
        if (url.origin !== location.origin || url.href !== current.href || !Number.isFinite(age) || age < 0 || age > 60000 || !pane) return;
        const openers = [...this.querySelectorAll('[data-or-nav-open]')].filter((item) => item.dataset.orNavOpen === pane.id);
        if (!openers.length || !this.openDialog(this.panel, this.toggle)) return;
        const opener = openers.find((item) => item.getClientRects().length);
        if (!opener) { this.panel.close(); return; }
        this.showNavPanel(pane.id, opener);
      } catch {}
    }

    updateCartCount(count) {
      for (const node of this.querySelectorAll('[data-or-cart-count]')) { node.textContent = String(count); node.hidden = count < 1; }
      for (const link of this.querySelectorAll('.or-header__cart')) {
        const label = link.querySelector(':scope > span:not([data-or-cart-count])')?.textContent || link.getAttribute('aria-label')?.replace(/,\s*\d+$/, '') || 'Winkelmand';
        link.setAttribute('aria-label', `${label}, ${count}`);
      }
    }

    setupMenuMedia(options) {
      this.menuMedia = [...this.querySelectorAll('[data-or-menu-media]')];
      this.menuMediaDecodes = new Map();
      for (const image of this.menuMedia) {
        image.addEventListener('load', () => this.decodeMenuMedia(image), options);
        image.addEventListener('error', () => {
          if (!this.detached) image.closest('.or-nav-editorial__card').dataset.orMediaState = 'error';
        }, options);
      }
      const intent = () => this.prepareMenuMedia(true);
      this.toggle?.addEventListener('pointerenter', intent, options);
      this.toggle?.addEventListener('pointerdown', intent, { ...options, passive: true });
      this.toggle?.addEventListener('focus', intent, options);
    }

    cancelMenuMediaWarmup() {
      if (!this.menuMediaWarmup) return;
      if (this.menuMediaWarmup.idle) cancelIdleCallback(this.menuMediaWarmup.id);
      else clearTimeout(this.menuMediaWarmup.id);
      this.menuMediaWarmup = null;
    }

    prepareMenuMedia(intent) {
      if (!this.isConnected || this.detached) return;
      this.cancelMenuMediaWarmup();
      for (const image of this.menuMedia || []) {
        const card = image.closest('.or-nav-editorial__card');
        if (image.dataset.orMediaPrepared === 'true') {
          if (!intent || card.dataset.orMediaState !== 'error' || image.dataset.orMediaRetried === 'true') continue;
          image.dataset.orMediaRetried = 'true';
          this.menuMediaDecodes.delete(image);
          image.src = image.getAttribute('src');
        }
        image.dataset.orMediaPrepared = 'true';
        card.dataset.orMediaState = 'loading';
        image.fetchPriority = 'low';
        image.loading = 'eager';
        this.decodeMenuMedia(image);
      }
    }

    decodeMenuMedia(image) {
      if (!this.isConnected || this.detached || image.dataset.orMediaPrepared !== 'true') return;
      if (typeof image.decode !== 'function' && !image.complete) return;
      const source = image.currentSrc || image.src;
      const previous = this.menuMediaDecodes.get(image);
      if (previous?.source === source) return previous.promise;
      const job = { source };
      this.menuMediaDecodes.set(image, job);
      job.promise = (typeof image.decode === 'function' ? image.decode() : Promise.resolve()).then(() => {
        if (this.detached || !this.isConnected || this.menuMediaDecodes.get(image) !== job || (image.currentSrc || image.src) !== source) return;
        if (image.naturalWidth > 0) image.closest('.or-nav-editorial__card').dataset.orMediaState = 'ready';
      }).catch(() => {
        if (!this.detached && this.isConnected && this.menuMediaDecodes.get(image) === job && image.complete && image.naturalWidth === 0) image.closest('.or-nav-editorial__card').dataset.orMediaState = 'error';
      });
      return job.promise;
    }

    receiveMenuCart(cart) {
      if (!Number.isSafeInteger(cart?.item_count) || cart.item_count < 0) return;
      this.menuOfferVersion += 1;
      this.menuOfferRequest?.controller.abort();
      this.menuOfferRequest = null;
      this.menuOfferPending = false;
      this.menuOfferUncertain = !Array.isArray(cart.items);
      this.updateCartCount(cart.item_count);
      this.renderMenuOffer(cart);
      if (this.menuOfferUncertain) this.refreshMenuOffer();
    }

    confirmedMenuTier(cart) {
      if (!Array.isArray(cart?.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) return null;
      const allocations = cart.items.flatMap((item) => {
        const source = item?.line_level_discount_allocations || item?.discount_allocations;
        return Array.isArray(source) ? source : [];
      });
      const applications = allocations.map((allocation) => ({ title: allocation?.discount_application?.title, amount: allocation?.amount }));
      if (Array.isArray(cart.cart_level_discount_applications)) {
        for (const application of cart.cart_level_discount_applications) applications.push({ title: application?.title, amount: application?.total_allocated_amount });
      }
      return this.menuOfferRows.filter((row) => cart.item_count >= Number(row.dataset.orMenuTier) && applications.some((application) => application.title === row.dataset.orMenuTierTitle && Number.isSafeInteger(application.amount) && application.amount > 0)).at(-1) || null;
    }

    renderMenuOffer(cart) {
      if (!this.menuOffer || !Number.isSafeInteger(cart?.item_count) || cart.item_count < 0) return;
      this.menuOfferCart = cart;
      const strings = this.menuOfferConfig.strings || {};
      const selected = this.menuOfferRows.filter((row) => cart.item_count >= Number(row.dataset.orMenuTier)).at(-1);
      const confirmed = this.menuOfferPending || this.menuOfferUncertain ? null : this.confirmedMenuTier(cart);
      for (const row of this.menuOfferRows) {
        const active = row === selected;
        row.classList.toggle('is-active', active);
        if (active) row.setAttribute('aria-current', 'step');
        else row.removeAttribute('aria-current');
        row.querySelector('[data-or-menu-tier-check]')?.toggleAttribute('hidden', !active);
        const state = row.querySelector('[data-or-menu-tier-state]');
        if (state) {
          state.hidden = !active;
          state.textContent = row === confirmed ? strings.tierConfirmedState : strings.tierEstimatedState;
        }
      }
      const status = this.menuOffer.querySelector('[data-or-menu-offer-status]');
      if (status) status.textContent = this.menuOfferPending ? strings.tierWaiting : this.menuOfferUncertain ? strings.tierUncertain : confirmed ? (strings.tierConfirmed || '').replaceAll('[count]', String(cart.item_count)).replaceAll('[percent]', confirmed.dataset.orMenuTierPercent) : strings.tierPending;
    }

    async refreshMenuOffer() {
      if (!this.menuOffer || !this.menuOfferConfig.cartGetUrl || !this.isConnected || this.menuOfferRequest) return;
      const request = { controller: new AbortController(), version: this.menuOfferVersion };
      this.menuOfferRequest = request;
      this.menuOfferPending = true;
      this.renderMenuOffer(this.menuOfferCart);
      const timeout = setTimeout(() => request.controller.abort(), 5000);
      try {
        const response = await fetch(this.menuOfferConfig.cartGetUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: request.controller.signal });
        if (!response.ok) throw new Error('cart');
        const cart = await response.json();
        if (!Number.isSafeInteger(cart?.item_count) || cart.item_count < 0 || !Array.isArray(cart.items)) throw new Error('cart');
        if (!this.isConnected || request.version !== this.menuOfferVersion || this.menuOfferRequest !== request) return;
        this.menuOfferPending = false;
        this.menuOfferUncertain = false;
        this.updateCartCount(cart.item_count);
        this.renderMenuOffer(cart);
      } catch {
        if (!this.isConnected || request.version !== this.menuOfferVersion || this.menuOfferRequest !== request) return;
        this.menuOfferPending = false;
        this.menuOfferUncertain = true;
        this.renderMenuOffer(this.menuOfferCart);
      } finally {
        clearTimeout(timeout);
        if (this.menuOfferRequest === request) this.menuOfferRequest = null;
      }
    }
  });
})();
